import { TimeFrame } from '@/models/Leaderboard'
import { PaginationParams } from '@/types/PaginationParams'
import { getToken } from '@ghl/clientportal-core'
import { isAuthV2Enabled } from '@ghl/clientportal-core/init'
import config from '../config'
import { requests } from './HTTPService'

const DEFAULT_HEADERS: any = {
  channel: 'APP',
  source: 'PORTAL_USER',
  'source-id': 'NA',
  version: '2021-07-28',
}

export const generateRestHeader = (defaultHeaders: Record<string, string>, token: string, sourceId = 'Preview'): Record<string, string> => {
  return {
    ...defaultHeaders,
    'token-id': token,
    'source-id': sourceId,
  }
}

const httpClientPortal = requests(config?.CLIENTPORTAL_SERVER_URL)
const httpCommunities = requests(config?.COMMUNITIES_SERVER_URL)

const ClientPortal = {
  getLeaderboard: async (groupId: string, timeframe: TimeFrame | undefined, rankLimit: number, rankSkip: number): Promise<any> => {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': 'Preview',
    }
    if (token) {
      headers['token-id'] = token
    }
    return httpClientPortal.get('/leaderboards/' + groupId, {
      headers,
      params: {
        timeFrame: timeframe,
        rankLimit,
        rankSkip,
      },
    })
  },
  getUserRankOnLeaderboard: async (groupId: string, contactId: string, timeframe?: TimeFrame): Promise<any> => {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': 'Preview',
    }
    if (token) {
      headers['token-id'] = token
    }
    return httpClientPortal.get(`/leaderboards/${groupId}/users/${contactId}`, {
      headers,
      params: { timeFrame: timeframe },
    })
  },
  getNotifications: async (read?: boolean, paginationObject?: PaginationParams): Promise<any> => {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': 'Preview',
    }
    if (token) {
      headers['token-id'] = token
    }
    const url = isAuthV2Enabled() ? '/notifications/v2' : '/notifications'
    return httpClientPortal.get(url, {
      headers,
      params: { read, ...paginationObject },
    })
  },
  markNotificationAsRead: async (notificationId?: string): Promise<any> => {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': 'Preview',
    }
    if (token) {
      headers['token-id'] = token
    }
    let queryString = ''
    if (notificationId) {
      queryString = `?notificationId=${notificationId}`
    }
    const url = isAuthV2Enabled() ? '/notifications/v2/read' : '/notifications/read'
    return httpClientPortal.post(url + queryString, undefined, {
      headers,
    })
  },
  getLocationDetails: async (domain: string): Promise<any> => {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': 'Preview',
    }
    if (token) {
      headers['token-id'] = token
    }
    return httpClientPortal.get('/portal-settings', {
      headers,
      params: { domain },
    })
  },
  getLocationDetailsByLocationId: async (locationId: string): Promise<any> => {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': 'Preview',
    }
    if (token) {
      headers['token-id'] = token
    }
    return httpClientPortal.get('/portal-settings/initialize-app', {
      headers,
      params: { locationId },
    })
  },
  getUserDetails: async (locationId: string, contactId: string, forceRefresh = false): Promise<any> => {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': locationId,
    }

    // Add cache-busting headers when forcing refresh
    if (forceRefresh) {
      headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
      headers['Pragma'] = 'no-cache'
      headers['Expires'] = '0'
    }

    if (token) {
      headers['token-id'] = token
    }

    return httpClientPortal.get(`/${locationId}/users/${contactId}?getLocationId=true`, {
      headers,
      params: {},
    })
  },
  updateUserDetails: async (locationId: string, contactId: string, profileData: any): Promise<any> => {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': locationId,
    }
    if (token) {
      headers['token-id'] = token
    }
    return httpClientPortal.put(`/${locationId}/users/${contactId}`, profileData, {
      headers,
    })
  },
  async compressImage(imageCloudUrl: string, locationId: string, resolution: number): Promise<any> {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
    }
    if (token) {
      headers['token-id'] = token
    }
    return httpClientPortal
      .post(
        `/${locationId}/medias/compress-image`,
        {
          imageUrl: imageCloudUrl,
          resolution: resolution,
        },
        { headers }
      )
      .then(res => res.data)
  },
  async fetchGifs(
    searchText = 'hello',
    limit: number,
    offset: number,
    locationId: string,
    rating = 'pg-13',
    language = 'en'
  ): Promise<any> {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
    }
    if (token) {
      headers['token-id'] = token
    }

    return httpClientPortal.get(`/${locationId}/medias/gifs`, {
      headers,
      params: {
        searchText,
        limit,
        offset,
        rating,
        language,
      },
    })
  },
  getSignedUrlForProfileUpload: async (locationId: string, imageName: string, imageType: string): Promise<any> => {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': locationId,
    }
    if (token) {
      headers['token-id'] = token
    }
    return httpClientPortal.post(
      `/${locationId}/medias/signed-url`,
      {
        name: imageName,
        folder: 'users',
        type: imageType,
      },
      { headers }
    )
  },
  generateIamV2Token: async (): Promise<any> => {
    const token = await getToken()
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': 'Preview',
    }
    if (token) {
      headers['token-id'] = token
    }
    return httpClientPortal.post('/auth/generate-iam-v2-token', undefined, headers)
  },
  getChatWidgetConfig: async (locationId: string, chatWidgetId: string): Promise<any> => {
    const response = await httpClientPortal.get(`/${locationId}/users/chat-widget`, {
      params: {
        widgetId: chatWidgetId,
        isGTM: false,
      },
    })
    return response.data
  },
}

const httpPayments = requests(config.paymentBaseUrl)
const httpBaseApi = requests(config.REST_API_URLS)
const Payments = {
  placeOrder: async (body: { locationId: string; [key: string]: unknown }): Promise<any> => {
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': body.locationId,
    }
    return httpPayments.post('/orders', body, { headers })
  },
  getDefaultPaymentProvider: async (locationId: string, liveMode: boolean): Promise<any> => {
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': locationId,
    }
    return httpPayments.get(
      `/integrations/provider/default/public?altId=${locationId}&altType=location&liveMode=${liveMode}&with=express-checkout`,
      { headers }
    )
  },
  getAmountSummary: async (body: { altId: string; [key: string]: unknown }): Promise<any> => {
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': body.altId,
    }
    return httpBaseApi.post('/products/amount-summary', body, { headers })
  },
  getCountryState: async (locationId: string): Promise<any> => {
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': locationId,
    }
    return httpBaseApi.get('/store/country/state', { headers })
  },
  getTaxAutomaticConfig: async (locationId: string): Promise<any> => {
    const headers = {
      ...DEFAULT_HEADERS,
      'source-id': locationId,
    }
    return httpCommunities.get(
      `/locations/${locationId}/tax/automatic-config`,
      { headers }
    )
  },
}
export default { ClientPortal, Payments }
